/* eslint-disable unicode-bom, no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import { usePermission } from '../context/PermissionContext';
import {
  getAllRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  assignRolePermissions,
  assignRoleToAdmin
} from '../services/api';
import './rolespage.css';

var MODULES = [
  { key: 'dashboard', label: 'Dashboard' }, { key: 'categories', label: 'Categories' },
  { key: 'subcategories', label: 'Subcategories' }, { key: 'products', label: 'Products' },
  { key: 'orders', label: 'Orders' }, { key: 'customers', label: 'Customers' },
  { key: 'payments', label: 'Payments' }, { key: 'reports', label: 'Reports' },
  { key: 'settings', label: 'Settings' }, { key: 'roles', label: 'Roles & Permissions' },
  { key: 'silver_rate', label: 'Silver Rate' }, { key: 'about', label: 'About' },
  { key: 'banners', label: 'Banners' }, { key: 'scrollbar', label: 'Scrollbar Banners' },
  { key: 'coupons', label: 'Coupons' }, { key: 'contact', label: 'Contact' },
  { key: 'enquiries', label: 'Enquiries' }, { key: 'notifications', label: 'Notifications' },
  { key: 'offers', label: 'Offers' }, { key: 'toppicks', label: 'Top Picks' },
  { key: 'supercoins', label: 'ISHMAI Coins' }, { key: 'giftpacking', label: 'Gift Packing' },
  { key: 'employees', label: 'Employees' },
];
var ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export'];
var ACTION_LABELS = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', approve: 'Approve', export: 'Export' };

var getId = function (value) { return value ? (value._id || value.id || value.roleId || '') : ''; };
var moduleKey = function (value) { return String(value || '').trim().toLowerCase(); };
var humanModule = function (value) { return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }); };
var unwrap = function (res) { var d = res && res.data; return d ? (d.data || d.role || d) : null; };
var listFromResponse = function (res) {
  var d = res && res.data;
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.data)) return d.data;
  if (Array.isArray(d.roles)) return d.roles;
  if (d.data && Array.isArray(d.data.roles)) return d.data.roles;
  return [];
};
var normalizePermissions = function (permissionsOrRole) {
  var raw = Array.isArray(permissionsOrRole) ? permissionsOrRole : permissionsOrRole && permissionsOrRole.permissions;
  var map = {};
  if (!Array.isArray(raw)) return map;
  raw.forEach(function (entry) {
    if (!entry || !entry.module) return;
    var actions = Array.isArray(entry.actions) ? entry.actions : [];
    map[moduleKey(entry.module)] = actions.reduce(function (acc, action) { acc[moduleKey(action)] = true; return acc; }, {});
  });
  return map;
};
var permissionsFromMap = function (map) {
  return modulesForMap(map).map(function (mod) {
    return { module: mod.key, actions: ACTIONS.filter(function (action) { return map[mod.key] && map[mod.key][action]; }) };
  }).filter(function (entry) { return entry.actions.length > 0; });
};
var modulesForMap = function (map) {
  var known = {}; MODULES.forEach(function (mod) { known[mod.key] = true; });
  var extra = Object.keys(map || {}).filter(function (key) { return !known[key]; }).map(function (key) { return { key: key, label: humanModule(key) }; });
  return MODULES.concat(extra);
};
var isSystemRole = function (role) { return role && role.isSystemRole !== false && role.isCustom !== true; };
var hasFullAccess = function (role) { return !!(role && (role.allAccess === true || role.fullAccess === true || role.isSuperAdmin === true)); };
var isRoleActive = function (role) {
  if (!role) return true;
  if (role.isActive === false || role.isActive === 'false' || role.isActive === 0) return false;
  if (role.status && String(role.status).trim().toLowerCase() === 'inactive') return false;
  if (role.status && String(role.status).trim().toLowerCase() === 'active') return true;
  if (role.isActive === true || role.isActive === 'true' || role.isActive === 1) return true;
  return true;
};

var permissionCount = function (role) {
  if (hasFullAccess(role)) return 'All Access';
  var map = normalizePermissions(role);
  var total = Object.keys(map).reduce(function (sum, key) { return sum + Object.keys(map[key] || {}).length; }, 0);
  return total ? total + ' Permissions' : '-';
};
var roleModules = function (role) {
  var seen = {}; var list = MODULES.slice();
  list.forEach(function (mod) { seen[mod.key] = true; });
  (Array.isArray(role && role.permissions) ? role.permissions : []).forEach(function (entry) {
    var key = moduleKey(entry && entry.module);
    if (key && !seen[key]) { seen[key] = true; list.push({ key: key, label: humanModule(entry.module) }); }
  });
  return list;
};
var emptyForm = function () { return { name: '', description: '', permissions: [], status: 'active' }; };
var formFromRole = function (role) {
  return {
    name: role ? (role.name || '') : '',
    description: role ? (role.description || '') : '',
    permissions: role ? (role.permissions || []) : [],
    status: isRoleActive(role) ? 'active' : 'inactive'
  };
};
var csvCell = function (value) { return '"' + String(value || '').replace(/"/g, '""') + '"'; };

var Icon = function (props) { return React.createElement('svg', { width: props.size || 15, height: props.size || 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, props.children); };
var ShieldIcon = function (props) { return React.createElement(Icon, { size: props.size }, React.createElement('path', { d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' })); };
var EyeIcon = function () { return React.createElement(Icon, { size: 14 }, React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }), React.createElement('circle', { cx: 12, cy: 12, r: 3 })); };
var PencilIcon = function () { return React.createElement(Icon, { size: 14 }, React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }), React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })); };
var TrashIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }),
    React.createElement('line', { x1: 10, y1: 11, x2: 10, y2: 17 }),
    React.createElement('line', { x1: 14, y1: 11, x2: 14, y2: 17 })
  );
};
var PlusIcon = function () { return React.createElement(Icon, { size: 16 }, React.createElement('path', { d: 'M12 5v14M5 12h14' })); };
var XIcon = function () { return React.createElement(Icon, { size: 16 }, React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }), React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })); };
var ChevronDownIcon = function (props) {
  return React.createElement(Icon, { size: props.size || 14 },
    React.createElement('polyline', { points: '6 9 12 15 18 9' })
  );
};
var CheckIcon = function (props) {
  return React.createElement(Icon, { size: props.size || 14 },
    React.createElement('polyline', { points: '20 6 9 17 4 12' })
  );
};

function StatusSelect(props) {
  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];
  var containerRef = React.useRef(null);

  var value = props.value || 'active';
  var isActive = value === 'active';

  useEffect(function () {
    var handleClickOutside = function (e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return function () {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  var handleSelect = function (newVal) {
    if (props.disabled) return;
    props.onChange(newVal);
    setIsOpen(false);
  };

  return React.createElement('div', {
    className: 'rp-custom-select-wrap',
    ref: containerRef
  },
    React.createElement('select', {
      'aria-label': 'Status',
      value: value,
      disabled: props.disabled,
      className: 'rp-hidden-accessible-select',
      onChange: function (e) { props.onChange(e.target.value); }
    },
      React.createElement('option', { value: 'active' }, 'Active'),
      React.createElement('option', { value: 'inactive' }, 'Inactive')
    ),
    React.createElement('button', {
      type: 'button',
      className: 'rp-custom-select-trigger ' + (isActive ? 'is-active' : 'is-inactive') + (isOpen ? ' is-open' : ''),
      disabled: props.disabled,
      onClick: function () { if (!props.disabled) setIsOpen(!isOpen); }
    },
      React.createElement('div', { className: 'rp-select-trigger-content' },
        React.createElement('span', { className: 'rp-select-dot ' + (isActive ? 'dot-active' : 'dot-inactive') }),
        React.createElement('span', { className: 'rp-select-trigger-text' }, isActive ? 'Active' : 'Inactive')
      ),
      React.createElement('span', { className: 'rp-select-chevron' },
        React.createElement(ChevronDownIcon, { size: 14 })
      )
    ),
    isOpen && React.createElement('div', { className: 'rp-custom-select-menu' },
      React.createElement('button', {
        type: 'button',
        className: 'rp-custom-select-item ' + (isActive ? 'is-selected item-active' : ''),
        onClick: function () { handleSelect('active'); }
      },
        React.createElement('div', { className: 'rp-select-item-left' },
          React.createElement('span', { className: 'rp-select-dot dot-active' }),
          React.createElement('div', { className: 'rp-select-item-info' },
            React.createElement('span', { className: 'rp-select-item-title' }, 'Active'),
            React.createElement('span', { className: 'rp-select-item-desc' }, 'Role is active and accessible')
          )
        ),
        isActive && React.createElement('span', { className: 'rp-select-check check-active' }, React.createElement(CheckIcon, { size: 14 }))
      ),
      React.createElement('button', {
        type: 'button',
        className: 'rp-custom-select-item ' + (!isActive ? 'is-selected item-inactive' : ''),
        onClick: function () { handleSelect('inactive'); }
      },
        React.createElement('div', { className: 'rp-select-item-left' },
          React.createElement('span', { className: 'rp-select-dot dot-inactive' }),
          React.createElement('div', { className: 'rp-select-item-info' },
            React.createElement('span', { className: 'rp-select-item-title' }, 'Inactive'),
            React.createElement('span', { className: 'rp-select-item-desc' }, 'Role access is temporarily paused')
          )
        ),
        !isActive && React.createElement('span', { className: 'rp-select-check check-inactive' }, React.createElement(CheckIcon, { size: 14 }))
      )
    )
  );
}

function PermissionTable(props) {
  var role = props.role || {}; var map = normalizePermissions(role); var full = hasFullAccess(role);
  return React.createElement('div', { className: 'rp-permission-table-wrap' },
    React.createElement('table', { className: 'rp-permission-table' },
      React.createElement('thead', null, React.createElement('tr', null,
        React.createElement('th', null, 'Module'),
        ACTIONS.map(function (action) { return React.createElement('th', { key: action }, ACTION_LABELS[action]); })
      )),
      React.createElement('tbody', null,
        roleModules(role).map(function (mod) {
          return React.createElement('tr', { key: mod.key },
            React.createElement('td', null, mod.label),
            ACTIONS.map(function (action) {
              var allowed = full || !!(map[mod.key] && map[mod.key][action]);
              return React.createElement('td', { key: action, className: allowed ? 'is-allowed' : 'is-denied' },
                allowed
                  ? React.createElement('span', { className: 'rp-view-pill rp-view-pill-allowed' }, '✓ ' + ACTION_LABELS[action])
                  : React.createElement('span', { className: 'rp-view-pill rp-view-pill-denied' }, '—')
              );
            })
          );
        })
      )
    )
  );
}

function PermissionMatrix(props) {
  var value = normalizePermissions(props.permissions);
  var modules = modulesForMap(value);
  var toggle = function (mod, action) {
    if (props.disabled) return;
    var next = Object.assign({}, value);
    next[mod] = Object.assign({}, next[mod]);
    next[mod][action] = !next[mod][action];
    if (!next[mod][action]) delete next[mod][action];
    props.onChange(permissionsFromMap(next));
  };
  return React.createElement('div', { className: 'rp-permission-table-wrap' },
    React.createElement('table', { className: 'rp-permission-table' },
      React.createElement('thead', null, React.createElement('tr', null,
        React.createElement('th', null, 'Module'),
        ACTIONS.map(function (action) { return React.createElement('th', { key: action }, ACTION_LABELS[action]); })
      )),
      React.createElement('tbody', null,
        modules.map(function (mod) {
          return React.createElement('tr', { key: mod.key },
            React.createElement('td', null, mod.label),
            ACTIONS.map(function (action) {
              var checked = !!(value[mod.key] && value[mod.key][action]);
              return React.createElement('td', { key: action },
                React.createElement('label', { className: 'rp-check ' + (checked ? 'is-checked' : '') },
                  React.createElement('input', { type: 'checkbox', checked: checked, disabled: props.disabled, onChange: function () { toggle(mod.key, action); } }),
                  (checked ? '✓ ' : '') + ACTION_LABELS[action]
                )
              );
            })
          );
        })
      )
    )
  );
}

function PermissionsModal(props) {
  var role = props.role;
  return React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) props.onClose(); } },
    React.createElement('div', { className: 'rp-perm-modal modal-box' },
      React.createElement('div', { className: 'modal-hdr' },
        React.createElement('div', { className: 'rp-perm-modal-title' },
          React.createElement('span', { className: 'modal-title' }, role ? role.name : 'Role Permissions'),
          role && React.createElement('span', { className: 'rp-badge ' + (isSystemRole(role) ? 'rp-badge-system' : 'rp-badge-custom') }, isSystemRole(role) ? 'System Role' : 'Custom Role')
        ),
        React.createElement('button', { className: 'modal-close', type: 'button', onClick: props.onClose }, React.createElement(XIcon, null))
      ),
      role && React.createElement('div', { className: 'rp-form-grid' },
        React.createElement('div', { className: 'rp-field-card' },
          React.createElement('span', { className: 'rp-field-label' }, 'Role Name'),
          React.createElement('div', { className: 'rp-field-val' }, role.name || '-')
        ),
        React.createElement('div', { className: 'rp-field-card' },
          React.createElement('span', { className: 'rp-field-label' }, 'Status'),
          React.createElement('div', { className: 'rp-field-val' },
            React.createElement('span', {
              className: 'rp-status-toggle ' + (isRoleActive(role) ? 'is-active' : 'is-inactive'),
              style: { cursor: 'default', transform: 'none' }
            },
              React.createElement('span', { className: 'rp-status-indicator' }),
              React.createElement('span', { className: 'rp-status-text' }, isRoleActive(role) ? 'Active' : 'Inactive')
            )
          )
        ),
        React.createElement('div', { className: 'rp-field-card rp-field-card-full' },
          React.createElement('span', { className: 'rp-field-label' }, 'Description'),
          React.createElement('div', { className: 'rp-field-val' }, role.description || 'No description provided.')
        )
      ),
      props.loading ? React.createElement('div', { className: 'rp-perm-state' }, 'Loading permissions...') : props.error ? React.createElement('div', { className: 'rp-perm-state rp-perm-state-error' }, props.error) : React.createElement(PermissionTable, { role: role }),
      React.createElement('div', { className: 'rp-modal-actions' },
        React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: props.onClose }, 'Close')
      )
    )
  );
}

function RoleFormModal(props) {
  var editRole = props.role; var isEdit = !!editRole; var roleIsSystem = isSystemRole(editRole);
  var formState = useState(function () { return editRole ? formFromRole(editRole) : emptyForm(); });
  var form = formState[0]; var setForm = formState[1];
  var savingState = useState(false); var saving = savingState[0]; var setSaving = savingState[1];
  var errorState = useState(''); var error = errorState[0]; var setError = errorState[1];

  useEffect(function () {
    if (editRole) {
      setForm(formFromRole(editRole));
    } else {
      setForm(emptyForm());
    }
  }, [editRole]);

  var handleNameChange = function (e) {
    // Only allow letters and spaces. Block numbers and special characters.
    var filtered = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setForm(Object.assign({}, form, { name: filtered }));
    if (error) setError('');
  };

  var handleNameKeyDown = function (e) {
    // Allow standard control/navigation keys
    var allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    // Block any key that is not a letter or space
    if (!/^[a-zA-Z\s]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  var save = function () {
    var trimmedName = form.name.trim();
    if (!trimmedName) {
      setError('Role name is required.');
      return;
    }
    if (!/^[a-zA-Z\s]+$/.test(trimmedName)) {
      setError('Role name can only contain letters and spaces. Numbers and special characters are not allowed.');
      return;
    }

    setSaving(true);
    setError('');
    var roleStatus = form.status === 'inactive' ? 'inactive' : 'active';
    var isStatusActive = roleStatus === 'active';

    var payload = {
      name: trimmedName,
      description: (form.description || '').trim(),
      permissions: form.permissions || [],
      status: roleStatus,
      isActive: isStatusActive
    };

    var roleId = getId(editRole);
    var request = (isEdit && roleId)
      ? updateRole(roleId, payload).then(function (res) {
          return assignRolePermissions(roleId, { permissions: form.permissions || [] })
            .catch(function (permErr) {
              console.warn('assignRolePermissions warning:', permErr);
              return res;
            });
        })
      : createRole(payload);

    request
      .then(function () {
        props.onSaved();
      })
      .catch(function (err) {
        setError((err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Unable to save role.');
      })
      .finally(function () { setSaving(false); });
  };

  return React.createElement('div', { className: 'modal-overlay', onClick: function (e) { if (e.target.classList.contains('modal-overlay')) props.onClose(); } },
    React.createElement('div', { className: 'rp-role-form-modal modal-box' },
      React.createElement('div', { className: 'modal-hdr' },
        React.createElement('div', { className: 'rp-perm-modal-title' },
          React.createElement('span', { className: 'modal-title' }, isEdit ? 'Edit Role' : 'Create Role'),
          editRole && React.createElement('span', { className: 'rp-badge ' + (roleIsSystem ? 'rp-badge-system' : 'rp-badge-custom') }, roleIsSystem ? 'System Role' : 'Custom Role')
        ),
        React.createElement('button', { className: 'modal-close', type: 'button', onClick: props.onClose, disabled: saving }, React.createElement(XIcon, null))
      ),
      roleIsSystem && React.createElement('div', { className: 'rp-error-banner' }, 'System roles can be viewed but should not be edited.'),
      error && React.createElement('div', { className: 'rp-error-banner' }, error),
      React.createElement('div', { className: 'rp-form-grid' },
        React.createElement('label', { className: 'rp-field-card rp-field' },
          React.createElement('span', { className: 'rp-field-label' }, 'Role Name (Letters & Spaces only)'),
          React.createElement('input', {
            value: form.name,
            disabled: roleIsSystem,
            onChange: handleNameChange,
            onKeyDown: handleNameKeyDown,
            placeholder: 'e.g. Content Manager'
          })
        ),
        React.createElement('div', { className: 'rp-field-card rp-field' },
          React.createElement('span', { className: 'rp-field-label' }, 'Status'),
          React.createElement(StatusSelect, {
            value: form.status || 'active',
            disabled: roleIsSystem,
            onChange: function (val) {
              setForm(Object.assign({}, form, { status: val }));
            }
          })
        ),
        React.createElement('label', { className: 'rp-field-card rp-field rp-field-card-full' },
          React.createElement('span', { className: 'rp-field-label' }, 'Description'),
          React.createElement('textarea', {
            value: form.description,
            disabled: roleIsSystem,
            onChange: function (e) { setForm(Object.assign({}, form, { description: e.target.value })); },
            placeholder: 'Brief summary of role permissions'
          })
        )
      ),
      React.createElement(PermissionMatrix, {
        permissions: form.permissions,
        disabled: roleIsSystem,
        onChange: function (permissions) { setForm(Object.assign({}, form, { permissions: permissions })); }
      }),
      React.createElement('div', { className: 'rp-modal-actions' },
        React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: props.onClose, disabled: saving }, 'Cancel'),
        !roleIsSystem && React.createElement('button', { className: 'btn btn-p', type: 'button', onClick: save, disabled: saving }, saving ? 'Saving...' : 'Save Role')
      )
    )
  );
}

function AssignRoleModal(props) {
  var adminIdState = useState(''); var adminId = adminIdState[0]; var setAdminId = adminIdState[1];
  var roleIdState = useState(getId(props.roles[0]) || ''); var roleId = roleIdState[0]; var setRoleId = roleIdState[1];
  var savingState = useState(false); var saving = savingState[0]; var setSaving = savingState[1];
  var errorState = useState(''); var error = errorState[0]; var setError = errorState[1];
  var save = function () {
    if (!adminId.trim() || !roleId) { setError('Admin ID and role are required.'); return; }
    setSaving(true); setError('');
    assignRoleToAdmin(adminId.trim(), roleId).then(function () { props.onSaved(adminId.trim()); }).catch(function (err) { setError((err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Unable to assign role.'); }).finally(function () { setSaving(false); });
  };
  return React.createElement('div', { className: 'modal-overlay' },
    React.createElement('div', { className: 'rp-assign-modal' },
      React.createElement('div', { className: 'modal-hdr' }, React.createElement('span', { className: 'modal-title' }, 'Assign Role to Admin'), React.createElement('button', { className: 'modal-close', type: 'button', onClick: props.onClose, disabled: saving }, React.createElement(XIcon, null))),
      error && React.createElement('div', { className: 'rp-error-banner' }, error),
      React.createElement('label', { className: 'rp-field' }, React.createElement('span', null, 'Admin ID'), React.createElement('input', { value: adminId, onChange: function (e) { setAdminId(e.target.value); }, placeholder: 'Paste admin id' })),
      React.createElement('label', { className: 'rp-field' }, React.createElement('span', null, 'Role'), React.createElement('select', { value: roleId, onChange: function (e) { setRoleId(e.target.value); } }, props.roles.map(function (role) { return React.createElement('option', { key: getId(role), value: getId(role) }, role.name); }))),
      React.createElement('div', { className: 'rp-modal-actions' }, React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: props.onClose, disabled: saving }, 'Cancel'), React.createElement('button', { className: 'btn btn-p', type: 'button', onClick: save, disabled: saving }, saving ? 'Assigning...' : 'Assign Role'))
    )
  );
}

var DEFAULT_ROLES = [
  { _id: 'role-super-admin', name: 'Super Admin', description: 'Full access to all system modules and settings', isSystemRole: true, allAccess: true, status: 'active', isActive: true },
  { _id: 'role-content-editor', name: 'Content Editor', description: 'Manage products, categories, subcategories, banners, and about page', isSystemRole: true, status: 'active', isActive: true, permissions: [{ module: 'categories', actions: ['view', 'create', 'edit'] }, { module: 'subcategories', actions: ['view', 'create', 'edit'] }, { module: 'products', actions: ['view', 'create', 'edit'] }, { module: 'banners', actions: ['view', 'create', 'edit'] }, { module: 'about', actions: ['view', 'create', 'edit'] }] },
  { _id: 'role-product-manager', name: 'Product Manager', description: 'Manage products, categories, subcategories, offers, and coupons', isSystemRole: true, status: 'active', isActive: true, permissions: [{ module: 'categories', actions: ['view', 'create', 'edit', 'delete'] }, { module: 'subcategories', actions: ['view', 'create', 'edit', 'delete'] }, { module: 'products', actions: ['view', 'create', 'edit', 'delete', 'export'] }, { module: 'offers', actions: ['view', 'create', 'edit'] }, { module: 'coupons', actions: ['view', 'create', 'edit'] }] },
  { _id: 'role-order-manager', name: 'Order Manager', description: 'Manage customer orders, shipments, and customer enquiries', isSystemRole: true, status: 'active', isActive: true, permissions: [{ module: 'orders', actions: ['view', 'create', 'edit', 'export'] }, { module: 'customers', actions: ['view', 'create', 'edit'] }, { module: 'enquiries', actions: ['view', 'edit'] }] },
  { _id: 'role-support-specialist', name: 'Support Specialist', description: 'View and handle customer issues, contacts, and notifications', isSystemRole: true, status: 'active', isActive: true, permissions: [{ module: 'customers', actions: ['view'] }, { module: 'contact', actions: ['view', 'edit'] }, { module: 'notifications', actions: ['view', 'create'] }] }
];

function RolesPermissions() {
  var permission = usePermission();
  var canCreate = permission.hasPermission('roles', 'create');
  var canEdit = permission.hasPermission('roles', 'edit');
  var canExport = permission.hasPermission('roles', 'export');
  var canDelete = permission.hasPermission('roles', 'delete');

  var rolesState = useState([]); var roles = rolesState[0]; var setRoles = rolesState[1];
  var loadingState = useState(true); var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState = useState(''); var error = errorState[0]; var setError = errorState[1];
  var msgState = useState(''); var msg = msgState[0]; var setMsg = msgState[1];
  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var viewState = useState(null); var viewRole = viewState[0]; var setViewRole = viewState[1];
  var viewLoadingState = useState(false); var viewLoading = viewLoadingState[0]; var setViewLoading = viewLoadingState[1];
  var viewErrorState = useState(''); var viewError = viewErrorState[0]; var setViewError = viewErrorState[1];
  var formRoleState = useState(null); var formRole = formRoleState[0]; var setFormRole = formRoleState[1];
  var createOpenState = useState(false); var createOpen = createOpenState[0]; var setCreateOpen = createOpenState[1];

  // Status updating state
  var updatingStatusIdState = useState(null);
  var updatingStatusId = updatingStatusIdState[0];
  var setUpdatingStatusId = updatingStatusIdState[1];

  // Delete modal state
  var deleteTargetState = useState(null);
  var deleteTarget = deleteTargetState[0];
  var setDeleteTarget = deleteTargetState[1];
  var deletingState = useState(false);
  var deleting = deletingState[0];
  var setDeleting = deletingState[1];
  var deleteErrorState = useState('');
  var deleteError = deleteErrorState[0];
  var setDeleteError = deleteErrorState[1];

  // Pagination states
  var currentPageState = useState(1);
  var currentPage = currentPageState[0];
  var setCurrentPage = currentPageState[1];
  var itemsPerPageState = useState(5);
  var itemsPerPage = itemsPerPageState[0];
  var setItemsPerPage = itemsPerPageState[1];
  var PER_PAGE_OPTIONS = [5, 10, 20, 50];

  var fetchRoles = function () {
    setLoading(true); setError('');
    getAllRoles()
      .then(function (res) {
        var list = listFromResponse(res);
        setRoles(list.length > 0 ? list : DEFAULT_ROLES);
      })
      .catch(function () {
        setRoles(DEFAULT_ROLES);
      })
      .finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchRoles(); }, []);

  var openPermissions = function (role) {
    setViewRole(role); setViewError(''); setViewLoading(true);
    getRoleById(getId(role)).then(function (res) { setViewRole(unwrap(res) || role); }).catch(function () {
      setViewError('Failed to load permissions for this role.');
    }).finally(function () { setViewLoading(false); });
  };

  var openEdit = function (role) {
    if (!canEdit) { setMsg('You do not have permission to edit roles.'); return; }
    setViewLoading(true);
    getRoleById(getId(role)).then(function (res) {
      var fetched = unwrap(res) || role;
      if (!fetched._id && role._id) fetched._id = role._id;
      if (!fetched.id && role.id) fetched.id = role.id;
      if (role.isSystemRole !== undefined && fetched.isSystemRole === undefined) {
        fetched.isSystemRole = role.isSystemRole;
      }
      setFormRole(fetched);
    }).catch(function () {
      setFormRole(role);
    }).finally(function () { setViewLoading(false); });
  };

  var exportRoles = function () {
    if (!canExport) { setMsg('You do not have permission to export roles.'); return; }
    var header = ['Role Name', 'Description', 'Type', 'Status', 'Permission Count', 'Permissions'];
    var rows = roles.map(function (role) {
      return [
        role.name,
        role.description,
        isSystemRole(role) ? 'System Role' : 'Custom Role',
        isRoleActive(role) ? 'Active' : 'Inactive',
        permissionCount(role),
        Array.isArray(role.permissions) ? role.permissions.map(function (entry) { return entry.module + ':' + (Array.isArray(entry.actions) ? entry.actions.join('|') : ''); }).join('; ') : ''
      ];
    });
    var csv = [header].concat(rows).map(function (row) { return row.map(csvCell).join(','); }).join('\n');
    var url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    var link = document.createElement('a');
    link.href = url; link.download = 'roles-permissions.csv'; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  };

  var afterSaved = function (text) {
    setCreateOpen(false); setFormRole(null); setMsg(text || 'Saved successfully.');
    fetchRoles(); permission.refreshPermissions();
    setTimeout(function () { setMsg(''); }, 3500);
  };

  // Toggle Active/Inactive status for an existing role
  var handleToggleStatus = function (role) {
    var roleId = getId(role);
    var currentlyActive = isRoleActive(role);
    var newActive = !currentlyActive;
    var newStatus = newActive ? 'active' : 'inactive';

    setUpdatingStatusId(roleId);
    setError('');

    // Keep all existing role details and permissions unchanged
    var payload = {
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
      status: newStatus,
      isActive: newActive
    };

    updateRole(roleId, payload)
      .then(function (res) {
        var updated = unwrap(res) || Object.assign({}, role, { status: newStatus, isActive: newActive });
        setRoles(function (prev) {
          return prev.map(function (r) {
            return getId(r) === roleId ? Object.assign({}, r, updated, { status: newStatus, isActive: newActive }) : r;
          });
        });
        setMsg('Role "' + role.name + '" status updated to ' + (newActive ? 'Active' : 'Inactive') + '.');
        setTimeout(function () { setMsg(''); }, 3500);
      })
      .catch(function (err) {
        console.error('Error toggling role status:', err);
        setError((err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Failed to update role status.');
      })
      .finally(function () {
        setUpdatingStatusId(null);
      });
  };

  // Delete only the selected role
  var confirmDeleteRole = function (role) {
    if (!role) return;
    var roleId = getId(role);
    setDeleting(true);
    setDeleteError('');

    deleteRole(roleId)
      .then(function () {
        setRoles(function (prev) {
          return prev.filter(function (r) { return getId(r) !== roleId; });
        });
        setDeleteTarget(null);
        setMsg('Role "' + role.name + '" deleted successfully.');
        setTimeout(function () { setMsg(''); }, 3500);
        fetchRoles();
      })
      .catch(function (err) {
        console.error('Error deleting role:', err);
        setDeleteError((err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Failed to delete role.');
      })
      .finally(function () {
        setDeleting(false);
      });
  };

  var filteredRoles = useMemo(function () {
    var q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(function (role) {
      return String(role.name || '').toLowerCase().indexOf(q) !== -1 || String(role.description || '').toLowerCase().indexOf(q) !== -1;
    });
  }, [roles, search]);

  // Reset to page 1 when search or itemsPerPage changes
  useEffect(function () {
    setCurrentPage(1);
  }, [search, itemsPerPage]);

  // Pagination calculations
  var totalPages = Math.max(1, Math.ceil(filteredRoles.length / itemsPerPage));
  var safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  var startIndex = (safeCurrentPage - 1) * itemsPerPage;
  var endIndex = Math.min(startIndex + itemsPerPage, filteredRoles.length);
  var pageItems = filteredRoles.slice(startIndex, endIndex);

  var getPageNumbers = function () {
    var pages = [];
    var maxVisible = 5;
    var start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    var end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (var i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <AdminLayout>
      {viewRole && <PermissionsModal role={viewRole} loading={viewLoading} error={viewError} onClose={() => setViewRole(null)} />}
      {createOpen && <RoleFormModal role={null} onClose={() => setCreateOpen(false)} onSaved={() => afterSaved('Role created successfully.')} />}
      {formRole && <RoleFormModal role={formRole} onClose={() => setFormRole(null)} onSaved={() => afterSaved('Role updated successfully.')} />}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="rp-delete-dialog modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="rp-delete-icon-wrap">
              <TrashIcon />
            </div>
            <h3 className="rp-delete-title">Delete Role</h3>
            <p className="rp-delete-desc">
              Are you sure you want to delete the role <strong>"{deleteTarget.name}"</strong>?
              {isSystemRole(deleteTarget)
                ? " (Note: System roles cannot be deleted and this operation will be rejected by the server.)"
                : " This action cannot be undone."}
            </p>
            {deleteError && <div className="rp-error-banner">{deleteError}</div>}
            <div className="rp-modal-actions">
              <button
                className="btn btn-g"
                type="button"
                disabled={deleting}
                onClick={() => { setDeleteTarget(null); setDeleteError(''); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger-delete"
                type="button"
                disabled={deleting}
                onClick={() => confirmDeleteRole(deleteTarget)}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header-row">
        <div className="page-header-main">
          <div className="page-header-title-row">
            <div className="page-header-icon"><ShieldIcon size={18} /></div>
            <div className="ptitle">Roles &amp; Permissions</div>
          </div>
          <div className="header-subtitle">Manage administrator roles and access permissions</div>
        </div>
        <div className="rp-header-actions">
          {canExport && <button className="add-btn rp-secondary-action" type="button" onClick={exportRoles}>Export</button>}
          {canCreate && <button className="add-btn" type="button" onClick={() => setCreateOpen(true)}><PlusIcon /> Create Role</button>}
        </div>
      </div>

      {error && <div className="rp-error-banner">{error}</div>}
      {msg && <div className="rp-success-banner">{msg}</div>}

      <div className="rp-toolbar">
        <div className="rp-search-field">
          <span>Search</span>
          <input placeholder="Search roles by name..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="wid">
        <div className="wh"><span className="wt">All Roles ({filteredRoles.length})</span></div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: '60px', textAlign: 'center' }}>S.No</th>
              <th>Role Name</th>
              <th>Description</th>
              <th>Permission Count</th>
              <th>Type</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center', width: '130px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>Loading roles...</td></tr>
            ) : filteredRoles.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No roles found.</td></tr>
            ) : pageItems.map((role, idx) => {
              var sno = startIndex + idx + 1;
              var active = isRoleActive(role);
              var isUpdatingThis = updatingStatusId === getId(role);

              return (
                <tr key={getId(role) || idx}>
                  <td style={{ textAlign: 'center', color: '#64748b', fontSize: 13, fontWeight: 600 }}>{sno}</td>
                  <td><strong>{role.name || '-'}</strong></td>
                  <td className="desc-cell" title={role.description}>{role.description || '-'}</td>
                  <td><span className={'rp-badge ' + (hasFullAccess(role) ? 'rp-badge-full' : 'rp-badge-count')}>{permissionCount(role)}</span></td>
                  <td><span className={'rp-badge ' + (isSystemRole(role) ? 'rp-badge-system' : 'rp-badge-custom')}>{isSystemRole(role) ? 'System Role' : 'Custom Role'}</span></td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className={'rp-status-toggle ' + (active ? 'is-active' : 'is-inactive')}
                      disabled={isUpdatingThis}
                      onClick={(e) => { e.stopPropagation(); handleToggleStatus(role); }}
                      title={'Click to switch status to ' + (active ? 'Inactive' : 'Active')}
                    >
                      <span className="rp-status-indicator" />
                      <span className="rp-status-text">
                        {isUpdatingThis ? 'Updating...' : (active ? 'Active' : 'Inactive')}
                      </span>
                    </button>
                  </td>
                  <td className="actions-cell">
                    <div className="row-actions">
                      <button
                        className="row-action-btn row-action-view"
                        title="View Permissions"
                        aria-label="View Permissions"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openPermissions(role); }}
                      >
                        <EyeIcon />
                      </button>
                      {canEdit && (
                        <button
                          className="row-action-btn row-action-edit"
                          title="Edit"
                          aria-label="Edit"
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openEdit(role); }}
                        >
                          <PencilIcon />
                        </button>
                      )}
                      <button
                        className="row-action-btn row-action-delete"
                        title="Delete Role"
                        aria-label="Delete Role"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(role); setDeleteError(''); }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Pagination below table */}
        {filteredRoles.length > 0 && (
          <div className="rp-pagination">
            <div className="rp-pagination-left">
              <span className="rp-pagination-info">
                Showing <strong>{filteredRoles.length === 0 ? 0 : startIndex + 1}–{endIndex}</strong> of <strong>{filteredRoles.length}</strong> roles
              </span>
              <div className="rp-per-page-select">
                <span>Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {PER_PAGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="rp-pagination-controls">
              <button
                type="button"
                className="rp-page-btn"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage(1)}
                title="First Page"
              >
                «
              </button>
              <button
                type="button"
                className="rp-page-btn"
                disabled={safeCurrentPage <= 1}
                onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                title="Previous Page"
              >
                ‹
              </button>

              {getPageNumbers().map((p) => (
                <button
                  key={p}
                  type="button"
                  className={'rp-page-btn' + (p === safeCurrentPage ? ' is-active' : '')}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                type="button"
                className="rp-page-btn"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                title="Next Page"
              >
                ›
              </button>
              <button
                type="button"
                className="rp-page-btn"
                disabled={safeCurrentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Last Page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default RolesPermissions;
